/**
 * Platform-wide MF figures for a date range — the MF counterpart of the Bonds
 * and IPO tiles on the main dashboard.
 *
 * Like those tiles, the totals cover every investor and are shown to anyone
 * holding this page's permission; they are aggregates, not anyone's records.
 *
 * The summary totals six activity types. They are laid out as a matrix rather
 * than six equal tiles — what the money did (bought, redeemed, switched) by
 * whether it was one-time or scheduled — with the combined total and each
 * type's share of it above.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { MF_SUMMARY_KEYS, fetchMFInvestmentSummary } from "../../../Api/mfAdminOverview";
import type { MFInvestmentSummary, MFSummaryKey } from "../../../Api/mfAdminOverview";
import { getMFErrorMessage } from "../../../Api/mfAdminInvestments";
import { formatCount, formatDate, formatMoneyExact } from "./mfOverviewFormat";
import styles from "./mf-overview.module.css";

type PresetKey = "all" | "month" | "30d" | "fy" | "custom";

interface DateRange {
  from?: string;
  to?: string;
}

/** A local date as a date input's value — not toISOString, which shifts to UTC. */
const inputDate = (date: Date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");

const presetRange = (preset: PresetKey): DateRange => {
  const today = new Date();
  const to = inputDate(today);
  if (preset === "month") {
    return { from: inputDate(new Date(today.getFullYear(), today.getMonth(), 1)), to };
  }
  if (preset === "30d") {
    const start = new Date(today);
    start.setDate(start.getDate() - 29);
    return { from: inputDate(start), to };
  }
  if (preset === "fy") {
    // The Indian financial year starts on 1 April.
    const startYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
    return { from: inputDate(new Date(startYear, 3, 1)), to };
  }
  return {};
};

const PRESETS: Array<{ key: PresetKey; label: string }> = [
  { key: "all", label: "All time" },
  { key: "month", label: "This month" },
  { key: "30d", label: "Last 30 days" },
  { key: "fy", label: "This financial year" },
];

const rangeLabel = (range: DateRange) => {
  if (range.from && range.to) return `${formatDate(range.from)} – ${formatDate(range.to)}`;
  if (range.from) return `From ${formatDate(range.from)}`;
  if (range.to) return `Up to ${formatDate(range.to)}`;
  return "All time";
};

/* ───────────────────────────── the matrix ───────────────────────────── */

interface ActivityRow {
  key: MFSummaryKey;
  label: string;
  cadence: string;
  one: string;
  many: string;
}

const ACTIVITY_GROUPS: Array<{
  key: string;
  title: string;
  caption: string;
  icon: React.ReactNode;
  rows: ActivityRow[];
}> = [
  {
    key: "buy",
    title: "Purchases",
    caption: "Buying into funds",
    icon: <AddCircleOutlineIcon style={{ fontSize: 19 }} />,
    rows: [
      { key: "purchase", label: "Lumpsum orders", cadence: "One-time", one: "order", many: "orders" },
      { key: "purchase_plan", label: "SIPs", cadence: "Scheduled", one: "SIP", many: "SIPs" },
    ],
  },
  {
    key: "sell",
    title: "Redemptions",
    caption: "Selling out of funds",
    icon: <RemoveCircleOutlineIcon style={{ fontSize: 19 }} />,
    rows: [
      { key: "redemption", label: "Redemptions", cadence: "One-time", one: "redemption", many: "redemptions" },
      { key: "redemption_plan", label: "SWPs", cadence: "Scheduled", one: "SWP", many: "SWPs" },
    ],
  },
  {
    key: "move",
    title: "Switches",
    caption: "Moving between funds",
    icon: <SwapHorizIcon style={{ fontSize: 19 }} />,
    rows: [
      { key: "switch", label: "Switches", cadence: "One-time", one: "switch", many: "switches" },
      { key: "switch_plan", label: "STPs", cadence: "Scheduled", one: "STP", many: "STPs" },
    ],
  },
];

const LABELS = {} as Record<MFSummaryKey, string>;
ACTIVITY_GROUPS.forEach((group) =>
  group.rows.forEach((row) => {
    LABELS[row.key] = row.label;
  })
);

const countLabel = (count: number | null, one: string, many: string) =>
  count === null
    ? "—"
    : count === 0
    ? `No ${many}`
    : `${formatCount(count)} ${count === 1 ? one : many}`;

/** 55.7% → "56%", but 0.4% stays "0.4%" so a small share never reads as zero. */
const formatShare = (share: number) =>
  share > 0 && share < 1 ? `${share.toFixed(1)}%` : `${Math.round(share)}%`;

/** Each activity's share of the combined amount, largest first. */
const ShareBar = ({ summary }: { summary: MFInvestmentSummary }) => {
  const parts = MF_SUMMARY_KEYS.map((key) => ({
    key,
    amount: summary.buckets[key].amount || 0,
  })).filter((part) => part.amount > 0);
  const sum = parts.reduce((total, part) => total + part.amount, 0);
  if (!sum) return null;
  parts.sort((left, right) => right.amount - left.amount);
  return (
    <div className={styles.share}>
      <div className={styles.shareBar} role="img" aria-label="Share of the combined amount by activity">
        {parts.map((part) => {
          const share = (part.amount / sum) * 100;
          return (
            <span
              key={part.key}
              className={`${styles.shareSegment} ${styles[`tone_${part.key}`]}`}
              style={{ width: `${share}%` }}
              title={`${LABELS[part.key]} · ${formatMoneyExact(part.amount)} · ${formatShare(share)}`}
            />
          );
        })}
      </div>
      <ul className={styles.shareLegend}>
        {parts.map((part) => (
          <li key={part.key}>
            <span className={`${styles.shareDot} ${styles[`tone_${part.key}`]}`} />
            {LABELS[part.key]}
            <b>{formatShare((part.amount / sum) * 100)}</b>
          </li>
        ))}
      </ul>
    </div>
  );
};

/* ───────────────────────────── the section ──────────────────────────── */

const MFSummarySection = () => {
  const [preset, setPreset] = useState<PresetKey>("all");
  const [applied, setApplied] = useState<DateRange>({});
  const [draftFrom, setDraftFrom] = useState("");
  const [draftTo, setDraftTo] = useState("");
  const [summary, setSummary] = useState<MFInvestmentSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const requestRef = useRef(0);
  const today = inputDate(new Date());

  const load = useCallback(async (range: DateRange) => {
    const request = ++requestRef.current;
    setLoading(true);
    setError("");
    try {
      const next = await fetchMFInvestmentSummary(range);
      if (request === requestRef.current) setSummary(next);
    } catch (requestError) {
      if (request !== requestRef.current) return;
      setSummary(null);
      setError(getMFErrorMessage(requestError, "The investment summary could not be loaded."));
    } finally {
      if (request === requestRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(applied);
  }, [applied, load]);

  useEffect(
    () => () => {
      requestRef.current += 1;
    },
    []
  );

  const choosePreset = (key: PresetKey) => {
    const range = presetRange(key);
    setPreset(key);
    setDraftFrom(range.from || "");
    setDraftTo(range.to || "");
    setApplied(range);
  };

  const customInvalid = Boolean(draftFrom && draftTo && draftFrom > draftTo);
  const applyCustom = () => {
    if (customInvalid || (!draftFrom && !draftTo)) return;
    setPreset("custom");
    setApplied({ from: draftFrom || undefined, to: draftTo || undefined });
  };

  const hasFigures =
    summary !== null &&
    (summary.total.count !== null ||
      MF_SUMMARY_KEYS.some((key) => summary.buckets[key].count !== null));

  return (
    <section className={styles.panel}>
      <div className={styles.panelHead}>
        <div>
          <h2>Investment summary</h2>
          <p>Across all investors · {rangeLabel(applied)}</p>
        </div>
        <button
          type="button"
          className={styles.iconButton}
          onClick={() => load(applied)}
          disabled={loading}
          aria-label="Refresh the summary"
          title="Refresh"
        >
          <RefreshIcon style={{ fontSize: 18 }} />
        </button>
      </div>

      <div className={styles.rangeBar}>
        <div className={styles.presets} role="group" aria-label="Date range">
          {PRESETS.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`${styles.preset} ${preset === item.key ? styles.presetActive : ""}`}
              aria-pressed={preset === item.key}
              onClick={() => choosePreset(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className={styles.customRange}>
          <input
            type="date"
            className={styles.dateInput}
            value={draftFrom}
            max={draftTo || today}
            aria-label="From date"
            onChange={(event) => setDraftFrom(event.target.value)}
          />
          <span>to</span>
          <input
            type="date"
            className={styles.dateInput}
            value={draftTo}
            min={draftFrom || undefined}
            max={today}
            aria-label="To date"
            onChange={(event) => setDraftTo(event.target.value)}
          />
          <button
            type="button"
            className={styles.primaryButton}
            disabled={customInvalid || (!draftFrom && !draftTo)}
            title={customInvalid ? "The start date is after the end date." : undefined}
            onClick={applyCustom}
          >
            Apply
          </button>
        </div>
      </div>

      {loading ? (
        <>
          <div className={`${styles.skeletonBlock} ${styles.skeletonTotal}`} />
          <div className={styles.activityGrid}>
            {[0, 1, 2].map((index) => (
              <div key={index} className={`${styles.skeletonBlock} ${styles.skeletonActivity}`} />
            ))}
          </div>
        </>
      ) : error ? (
        <div className={styles.noticeBad}>
          <span>{error}</span>
          <button type="button" className={styles.ghostButton} onClick={() => load(applied)}>
            Try again
          </button>
        </div>
      ) : summary && hasFigures ? (
        <>
          <div className={styles.totalCard}>
            <div className={styles.totalFigures}>
              <span className={styles.totalLabel}>All MF activity</span>
              <strong className={styles.totalValue}>{formatMoneyExact(summary.total.amount)}</strong>
              <span className={styles.totalMeta}>
                {countLabel(summary.total.count, "transaction", "transactions")} · every
                purchase, redemption and switch, one-time and scheduled
              </span>
            </div>
            <ShareBar summary={summary} />
          </div>

          <div className={styles.activityGrid}>
            {ACTIVITY_GROUPS.map((group) => (
              <div key={group.key} className={styles.activityCard}>
                <div className={styles.activityHead}>
                  <span className={styles.activityIcon}>{group.icon}</span>
                  <div>
                    <h3>{group.title}</h3>
                    <p>{group.caption}</p>
                  </div>
                </div>
                {group.rows.map((row) => {
                  const bucket = summary.buckets[row.key];
                  const empty = !bucket.count;
                  return (
                    <div key={row.key} className={`${styles.activityRow} ${empty ? styles.activityEmpty : ""}`}>
                      <span className={`${styles.shareDot} ${styles[`tone_${row.key}`]}`} />
                      <div className={styles.activityText}>
                        <span>{row.label}</span>
                        <small>
                          {row.cadence} · {countLabel(bucket.count, row.one, row.many)}
                        </small>
                      </div>
                      <strong>{empty ? "—" : formatMoneyExact(bucket.amount)}</strong>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </>
      ) : (
        <div className={styles.emptyPanel}>
          <p>The summary came back without any figures for this range.</p>
        </div>
      )}
    </section>
  );
};

export default MFSummarySection;
